import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, MessageCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from './Button';
import { RouteSkeleton } from './PageSkeletons';
import { Topbar } from './Topbar';
import { FacebookGlyph } from './facebook/campaigns-list/SharedComponents';
import { TikTokGlyph } from './tiktok/workspace/workspaceShared';
import { XGlyph } from './x-ads/XAdsShell';
import { Provider, ProviderInfo, isProviderConnected, knownConnected, providerForPath } from '@/lib/provider-connection';

const GoogleGlyph = ({ size = 28 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden fill="currentColor">
    <path d="M21.35 11.1H12v3.2h5.35c-.5 2.4-2.6 3.9-5.35 3.9a6.2 6.2 0 1 1 0-12.4c1.5 0 2.9.55 3.95 1.45l2.4-2.4A9.6 9.6 0 0 0 12 2.4a9.6 9.6 0 1 0 0 19.2c5.55 0 9.2-3.9 9.2-9.4 0-.4-.05-.75-.1-1.1z" />
  </svg>
);

const COPY: Record<Provider, { icon: React.ReactNode; title: string; body: string; points: string[]; cta: string; need: string }> = {
  meta: {
    icon: <FacebookGlyph size={30} />,
    title: 'Connect your Meta ad account',
    body: 'Facebook Ads and Instagram run on your Meta Business ad account. Connect it once and everything below works from here.',
    points: ['Create and manage Facebook & Instagram campaigns', 'Build lead forms — new leads land straight in your inbox', 'See spend and results without opening Ads Manager'],
    cta: 'Connect Meta account',
    need: 'You need admin access to the Meta Business account and its ad account.',
  },
  tiktok: {
    icon: <TikTokGlyph size={30} />,
    title: 'Connect your TikTok advertiser',
    body: 'Bring your TikTok campaigns, creatives and results into the CRM.',
    points: ['Launch and pause TikTok campaigns', 'Upload videos and build ads', 'Track spend, balance and leads in one place'],
    cta: 'Connect TikTok',
    need: 'You need access to a TikTok Ads Manager advertiser account.',
  },
  google: {
    icon: <GoogleGlyph size={30} />,
    title: 'Connect your Google Ads account',
    body: 'Search, Display, YouTube, Performance Max and App campaigns all use your Google Ads account.',
    points: ['Create campaigns for every Google objective', 'Capture leads from Google lead forms', 'Follow spend and results next to your CRM data'],
    cta: 'Connect Google Ads',
    need: 'Sign in with the Google account that manages your Google Ads account.',
  },
  x: {
    icon: <XGlyph size={28} />,
    title: 'Connect your X Ads account',
    body: 'Sign in with X, pick the ads account to advertise from, and everything below works from here.',
    points: ['Create campaigns, line items and promoted posts — all start paused', 'Collect leads from X lead forms and build audiences from CRM contact groups', 'See spend, impressions and engagements without leaving the CRM'],
    cta: 'Connect X Ads',
    need: 'You need a role on the ads account at ads.x.com.',
  },
  whatsapp: {
    icon: <MessageCircle size={30} />,
    title: 'Connect your WhatsApp number',
    body: 'Chats, templates, broadcasts and the AI assistant all send from your WhatsApp Business number.',
    points: ['Reply to customers from the shared inbox', 'Send approved templates and campaigns', 'Let the AI assistant answer and qualify leads'],
    cta: 'Connect WhatsApp',
    need: 'You need a WhatsApp Business account in Meta Business Manager.',
  },
};

const MODULE_NAME: Record<Provider, string> = { meta: 'Facebook Ads', tiktok: 'TikTok Ads', google: 'Google Ads', whatsapp: 'WhatsApp', x: 'X Ads' };

function ConnectProviderPage({ info, pathname }: { info: ProviderInfo; pathname: string }) {
  const { user } = useAuth();
  const c = COPY[info.provider];
  const isAdmin = user?.role === 'company_admin';
  const moduleName = pathname.startsWith('/instagram') ? 'Instagram' : MODULE_NAME[info.provider];
  return (
    <>
    <Topbar title="Connect an account" breadcrumb={moduleName} autoBack={false} />
    <div className="mx-auto flex min-h-[60vh] max-w-2xl items-center px-4 py-10">
      <div className="w-full rounded-3xl border border-border bg-surface p-8 text-center shadow-soft sm:p-10">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-white shadow-soft">{c.icon}</div>
        <h1 className="mt-5 font-display text-[22px] font-bold tracking-tight text-text-primary">{c.title}</h1>
        <p className="mx-auto mt-2 max-w-md text-[14px] leading-relaxed text-text-muted">{c.body}</p>
        <ul className="mx-auto mt-6 max-w-sm space-y-2.5 text-left">
          {c.points.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-[13.5px] text-text-secondary">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-primary" />{p}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col items-center gap-3">
          {isAdmin ? (
            <Link to={info.connectPath}><Button iconRight={<ArrowRight size={15} />}>{c.cta}</Button></Link>
          ) : (
            <p className="rounded-xl bg-surface-alt px-4 py-2.5 text-[13px] text-text-secondary">Ask your workspace admin to connect it — then this page opens here.</p>
          )}
          <p className="flex items-center gap-1.5 text-[12px] text-text-muted"><ShieldCheck size={13} />{c.need}</p>
        </div>
      </div>
    </div>
    </>
  );
}

/** Shows a connect page instead of an ads / WhatsApp page that has no account to work with. */
export function ProviderConnectGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const info = providerForPath(pathname);
  const provider = info?.provider;
  const [state, setState] = useState<{ provider: Provider; connected: boolean | null } | null>(
    () => (provider && knownConnected(provider) ? { provider, connected: true } : null),
  );

  useEffect(() => {
    if (!provider) return;
    let live = true;
    isProviderConnected(provider).then((connected) => { if (live) setState({ provider, connected }); });
    return () => { live = false; };
  }, [provider]);

  if (!info) return <>{children}</>;
  if (state?.provider !== info.provider) return <RouteSkeleton pathname={pathname} />;
  if (state.connected === false) return <ConnectProviderPage info={info} pathname={pathname} />;
  return <>{children}</>;
}
